import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import dashboardService, {
  DashboardData,
  AssetLocation,
} from '../../services/dashboardService';

interface DashboardState {
  dashboardData: DashboardData | null;
  assetLocations: AssetLocation[];
  totalLocations: number;
  isLoading: boolean;
  isLocationsLoading: boolean;
  error: string | null;
  locationsError: string | null;
}

const initialState: DashboardState = {
  dashboardData: null,
  assetLocations: [],
  totalLocations: 0,
  isLoading: false,
  isLocationsLoading: false,
  error: null,
  locationsError: null,
};

// Async thunks
export const fetchDashboardData = createAsyncThunk(
  'dashboard/fetchData',
  async (_, { rejectWithValue }) => {
    try {
      const response = await dashboardService.getDashboardData();
      return response;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || error.message || 'Failed to fetch dashboard data'
      );
    }
  }
);

export const fetchAssetLocations = createAsyncThunk(
  'dashboard/fetchLocations',
  async (_, { rejectWithValue }) => {
    try {
      const response = await dashboardService.getAssetLocations();
      return response;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || error.message || 'Failed to fetch asset locations'
      );
    }
  }
);

const dashboardSlice = createSlice({
  name: 'dashboard',
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
      state.locationsError = null;
    },
    clearDashboardData: (state) => {
      state.dashboardData = null;
    },
    clearAssetLocations: (state) => {
      state.assetLocations = [];
      state.totalLocations = 0;
    },
  },
  extraReducers: (builder) => {
    // Fetch dashboard data
    builder
      .addCase(fetchDashboardData.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchDashboardData.fulfilled, (state, action) => {
        state.isLoading = false;
        state.dashboardData = action.payload;
      })
      .addCase(fetchDashboardData.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      });

    // Fetch asset locations
    builder
      .addCase(fetchAssetLocations.pending, (state) => {
        state.isLocationsLoading = true;
        state.locationsError = null;
      })
      .addCase(fetchAssetLocations.fulfilled, (state, action) => {
        state.isLocationsLoading = false;
        state.assetLocations = action.payload.result;
        state.totalLocations = action.payload.totalCount;
      })
      .addCase(fetchAssetLocations.rejected, (state, action) => {
        state.isLocationsLoading = false;
        state.locationsError = action.payload as string;
      });
  },
});

export const { 
  clearError, 
  clearDashboardData, 
  clearAssetLocations 
} = dashboardSlice.actions;

export default dashboardSlice.reducer;